import { useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, settingsApi } from '../lib/api'

export default function DriverTrailerLoaderPage() {
  const navigate = useNavigate()

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['driver-trailer-loader-jobs'],
    queryFn: async () => (await api.jobs.list({ viewer: 'driver' })).data,
    refetchInterval: 10000,
  })

  const { data: equipment } = useQuery({
    queryKey: ['driver-trailer-loader-equipment'],
    queryFn: async () => (await settingsApi.getEquipment()).data.assignment,
    refetchInterval: 30000,
  })

  const activeJobs = useMemo(
    () => jobs.filter(j => !['DELIVERED', 'CANCELLED', 'COMPLETED', 'PAID'].includes(j.status)),
    [jobs],
  )

  // Auto-redirect when exactly one active job is assigned
  useEffect(() => {
    if (!isLoading && activeJobs.length === 1) {
      navigate(`/driver/jobs/${activeJobs[0].id}/trailer`, { replace: true })
    }
  }, [isLoading, activeJobs, navigate])

  // While loading or about to redirect, show minimal spinner
  if (isLoading || activeJobs.length === 1) {
    return <div className="container mx-auto p-6 text-gray-400 text-sm">Loading…</div>
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Trailer Loader</h1>
        <p className="text-sm text-gray-500 mt-1">
          {activeJobs.length > 1
            ? 'You have multiple active jobs. Choose one to open the trailer loader.'
            : 'No active jobs are currently assigned.'}
        </p>
      </div>

      {/* Equipment card — always shown when equipment is assigned */}
      {equipment && (
        <div className="bg-white rounded-xl border p-5">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">My Assigned Unit</h2>
          <div className="grid gap-3 sm:grid-cols-2 text-sm">
            <div className="rounded-lg border bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Tractor</div>
              <div className="font-medium text-gray-900">
                {equipment.tractorManufacturer && equipment.tractorModel
                  ? `${equipment.tractorManufacturer} ${equipment.tractorModel}`
                  : 'Assigned'}
              </div>
            </div>
            <div className="rounded-lg border bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Trailer</div>
              <div className="font-medium text-gray-900">
                {equipment.trailerManufacturer && equipment.trailerModel
                  ? `${equipment.trailerManufacturer} ${equipment.trailerModel}`
                  : 'Assigned'}
              </div>
            </div>
            {equipment.unitLabel && (
              <div className="rounded-lg border bg-blue-50 p-3 sm:col-span-2">
                <div className="text-xs text-blue-700 uppercase tracking-wide mb-1">Unit Label</div>
                <div className="font-semibold text-blue-900">{equipment.unitLabel}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No jobs state */}
      {activeJobs.length === 0 && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <div className="text-4xl mb-3">🚛</div>
          <p className="text-gray-700 font-medium mb-1">No active jobs assigned yet</p>
          <p className="text-sm text-gray-500 mb-5">Ask your dispatcher to assign a job. Once assigned, you'll open the trailer loader directly from the job.</p>
          <Link to="/driver/jobs" className="inline-block rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-800">
            Go to My Jobs
          </Link>
        </div>
      )}

      {/* Multi-job chooser */}
      {activeJobs.length > 1 && (
        <div className="bg-white rounded-xl border p-5">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Choose a Job to Load</h2>
          <div className="space-y-3">
            {activeJobs.map(job => (
              <div key={job.id} className="rounded-xl border-2 border-blue-100 p-4 flex items-center justify-between gap-4 hover:border-blue-300 transition">
                <div>
                  <div className="font-medium text-gray-900">{job.jobNumber ?? job.id.slice(0, 8)}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{job.status}</div>
                </div>
                <div className="flex gap-2">
                  <Link to={`/driver/jobs/${job.id}/trailer`} className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800">
                    Open Trailer Loader
                  </Link>
                  {job.loadId && (
                    <Link to={`/driver/loads/${job.loadId}/load-plan`} className="rounded-lg border border-blue-300 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50">
                      Mobile Load Plan
                    </Link>
                  )}
                  <Link to={`/driver/jobs/${job.id}`} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                    Open Job
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
